/**
 * Sentry 로 전송되는 이벤트에서 개인정보와 자격증명을 마스킹
 * Taap의 기준을 따라가되 프로젝트에 맞게 조정한다
 *
 * 1. 키 이름 기준 - 민감한 키에 담긴 값은 통째로 가리거나 마스킹
 * 2. 값 패턴 기준 - 키 이름과 무관하게 자격증명(JWT, Bearer) 형태의 문자열을 제거
 *
 * 원본을 수정하지 않고 사본을 변조하여 사용한다
 * Recoil state 등의 객체가 참조로 실릴 수 있어 원본이 수정되면 애플리케이션 상태가 오염됨
 *
 * 마스킹은 유틸을 재사용하지 않고 직접 구현한다
 * 의존성없이 독립적인 모듈로 유지하기 위함
 */

/** Sentry 기본 스크러빙과 동일한 표기 */
const FILTERED = "[Filtered]";

/** 이벤트에서 스크러빙 대상이 되는 최상위 필드. 스택트레이스는 진단 정보 손상을 막기 위해 제외한다 */
const SCRUB_TARGET_FIELDS = ["user", "extra", "contexts", "tags", "request", "breadcrumbs"] as const;

const MAX_DEPTH = 20;

/** 값을 통째로 가려야 하는 키 */
const REDACT_KEYS = [
  "password",
  "passwd",
  "secret",
  "token",
  "authorization",
  "apikey",
  "credential",
  "cookie",
  "ssn",
  "residentregistration",
  "accountnumber",
  "bankaccount",
];

/** 이메일로 취급해 마스킹할 키 */
const EMAIL_KEYS = ["email", "mail"];

/** 전화번호로 취급해 마스킹할 키 */
const PHONE_KEYS = ["phonenumber", "mobilenumber", "cellphone", "phone", "mobile"];

const JWT_VALUE = /\beyJ[\w-]+\.[\w-]+\.[\w-]*/g;
const BEARER_VALUE = /\bBearer\s+[\w\-._~+/]+=*/gi;

/** 비교를 위해 키에서 구분자를 없애고 소문자로 변경 (userEmail, user_email, USER-EMAIL -> useremail) */
const normalizeKey = (key: string) => key.toLowerCase().replace(/[^a-z0-9]/g, "");

const matchesAny = (normalizedKey: string, candidates: string[]) => candidates.some((candidate) => normalizedKey.includes(candidate));

/**
 * 이메일 아이디의 앞 2글자만 남긴다 (taap과 동일)
 */
const EMAIL_VISIBLE_LENGTH = 2;

const maskEmailValue = (email: string) => {
  const separatorIndex = email.indexOf("@");
  if (separatorIndex < 0) return email;
  if (separatorIndex <= EMAIL_VISIBLE_LENGTH) return email;

  const id = email.slice(0, separatorIndex);
  const domain = email.slice(separatorIndex);
  return `${id.slice(0, EMAIL_VISIBLE_LENGTH)}${"*".repeat(id.length - EMAIL_VISIBLE_LENGTH)}${domain}`;
};

const maskPhoneValue = (phone: string) => {
  const digits = phone.replace(/\D/g, "");
  if (digits.length < 8) return FILTERED;
  return `${digits.slice(0, 3)}-****-${digits.slice(-4)}`;
};

/**
 * 토큰류 마스킹
 */
const scrubString = (value: string) => value.replace(BEARER_VALUE, `Bearer ${FILTERED}`).replace(JWT_VALUE, FILTERED);

/** 키에 걸린 값을 키 종류에 맞게 마스킹 */
const scrubByKey = (normalizedKey: string, value: unknown): unknown => {
  if (matchesAny(normalizedKey, REDACT_KEYS)) return FILTERED;
  if (typeof value !== "string") return FILTERED;
  if (matchesAny(normalizedKey, EMAIL_KEYS)) return maskEmailValue(value);
  if (matchesAny(normalizedKey, PHONE_KEYS)) return maskPhoneValue(value);
  return FILTERED;
};

const isSensitiveKey = (normalizedKey: string) =>
  matchesAny(normalizedKey, REDACT_KEYS) || matchesAny(normalizedKey, EMAIL_KEYS) || matchesAny(normalizedKey, PHONE_KEYS);

/**
 * @param ancestors 현재 탐색 경로에 있는 객체들. 순환 참조를 감지하기 위해 사용
 */
const scrubValue = (value: unknown, ancestors: Set<object>, depth: number): unknown => {
  if (typeof value === "string") return scrubString(value);
  if (value === null || typeof value !== "object") return value;
  if (depth >= MAX_DEPTH) return "[Scrubbed: max depth]";
  if (ancestors.has(value)) return "[Scrubbed: circular]";

  ancestors.add(value);
  try {
    if (Array.isArray(value)) {
      return value.map((item) => scrubValue(item, ancestors, depth + 1));
    }
    const scrubbed: Record<string, unknown> = {};
    for (const [key, child] of Object.entries(value)) {
      const normalizedKey = normalizeKey(key);
      scrubbed[key] = isSensitiveKey(normalizedKey) ? scrubByKey(normalizedKey, child) : scrubValue(child, ancestors, depth + 1);
    }
    return scrubbed;
  } finally {
    ancestors.delete(value);
  }
};

/**
 * 자체 태그는 마스킹하지 않음
 */
const CTRL_TAG_PREFIX = "ctrl.";

const scrubTags = (tags: unknown): unknown => {
  if (typeof tags !== "object" || tags === null || Array.isArray(tags)) return scrubValue(tags, new Set<object>(), 0);

  const scrubbed: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(tags)) {
    if (key.startsWith(CTRL_TAG_PREFIX)) {
      scrubbed[key] = value;
      continue;
    }
    const normalizedKey = normalizeKey(key);
    scrubbed[key] = isSensitiveKey(normalizedKey) ? scrubByKey(normalizedKey, value) : scrubValue(value, new Set<object>(), 0);
  }

  return scrubbed;
};

/**
 * Sentry.init 의 beforeSend 에서 사용
 * 이벤트의 shallow copy를 만들고, 스크러빙 대상 필드만 가림
 */
export const scrubSentryEvent = <T extends object>(event: T): T => {
  const scrubbed: Record<string, unknown> = { ...(event as Record<string, unknown>) };

  for (const field of SCRUB_TARGET_FIELDS) {
    if (scrubbed[field] === undefined || scrubbed[field] === null) continue;
    scrubbed[field] = field === "tags" ? scrubTags(scrubbed[field]) : scrubValue(scrubbed[field], new Set<object>(), 0);
  }

  return scrubbed as T;
};
